import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Building2, Store, Monitor, Calendar, Users, TrendingUp, Target, Handshake, MapPin, Mail, Phone, ArrowRight, Sparkles, BarChart3, ShoppingBag, Megaphone, FileText, Download, Copy, Building, ClipboardList, PartyPopper, Cog, ShieldCheck, Leaf, Lightbulb, LineChart, ChevronDown, ChevronUp } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const CollapsibleMallCard = ({ svc, previewCount = 2 }) => {
  const [expanded, setExpanded] = React.useState(false);
  const shown = expanded ? svc.points : svc.points.slice(0, previewCount);
  const hasMore = svc.points.length > previewCount;

  return (
    <div
      data-testid={`mall-mgmt-card-${svc.title}`}
      style={{
        padding: '32px 28px',
        background: 'rgba(255, 255, 255, 0.03)',
        border: '1px solid rgba(255, 107, 0, 0.3)',
        transition: 'all 0.3s ease-in-out',
        display: 'flex',
        flexDirection: 'column'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = '#ff6b00';
        e.currentTarget.style.background = 'rgba(255, 107, 0, 0.06)';
        e.currentTarget.style.transform = 'translateY(-4px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'rgba(255, 107, 0, 0.3)';
        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.03)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <div style={{ color: '#ff6b00', marginBottom: '16px' }}>{svc.icon}</div>
      <h4 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '14px' }}>
        {svc.title}
      </h4>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {shown.map((p) => (
          <li key={p} style={{ display: 'flex', gap: '10px', marginBottom: '8px', color: 'var(--text-secondary)', fontSize: '14px', lineHeight: '1.55' }}>
            <CheckCircle2 size={14} style={{ color: '#ff6b00', flexShrink: 0, marginTop: '5px' }} />
            <span>{p}</span>
          </li>
        ))}
      </ul>
      {hasMore && (
        <button
          type="button"
          data-testid={`mall-mgmt-toggle-${svc.title}`}
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          style={{
            marginTop: '14px',
            alignSelf: 'flex-start',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 0',
            background: 'transparent',
            border: 'none',
            color: '#ff6b00',
            fontWeight: '600',
            fontSize: '13px',
            letterSpacing: '0.5px',
            cursor: 'pointer'
          }}
        >
          {expanded ? 'Read less' : 'Read more'}
          {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
      )}
    </div>
  );
};

const ZentharaDetail = () => {
  const NDA_URL = '/zenthara-nda.pdf';
  const NDA_FULL_URL = typeof window !== 'undefined' ? `${window.location.origin}${NDA_URL}` : NDA_URL;
  const [ndaCopied, setNdaCopied] = React.useState(false);

  const handleCopyNdaLink = () => {
    const link = `${window.location.origin}${NDA_URL}`;
    navigator.clipboard.writeText(link).then(() => {
      setNdaCopied(true);
      setTimeout(() => setNdaCopied(false), 2200);
    });
  };

  const services = [
    {
      icon: <Handshake size={48} />,
      title: 'Space Leasing & Brand Placement',
      description: 'Strategic tenant acquisition and onboarding with optimal brand mix for accelerated occupancy.',
      features: [
        'Strategic tenant acquisition & curation',
        'Seamless brand onboarding',
        'Optimal tenant mix optimization',
        'New brand/category introduction',
        'Accelerated occupancy solutions'
      ]
    },
    {
      icon: <Store size={48} />,
      title: 'Space on Hire (SOH)',
      description: 'Flexible short and long-term retail space rentals for pop-ups, activations, and brand presence.',
      features: [
        'Pop-up store setups',
        'Brand activations & launches',
        'Flea market organization'
      ]
    }
  ];

  const strengths = [
    {
      icon: <Building2 size={32} />,
      title: 'Deep Retail & Mall Expertise',
      description: '29+ years of experience in retail hospitality and mall management'
    },
    {
      icon: <Sparkles size={32} />,
      title: 'Innovative SOH Solutions',
      description: 'Creative and flexible space-on-hire solutions for maximum brand impact'
    },
    {
      icon: <Target size={32} />,
      title: 'Strategic Brand Placement',
      description: 'Expert tenant curation and optimal brand mix optimization'
    },
    {
      icon: <Users size={32} />,
      title: 'Customer-First Approach',
      description: 'Transparent, data-driven partnerships focused on your success'
    }
  ];

  const sectors = [
    { name: 'Shopping Malls', icon: <ShoppingBag size={40} /> },
    { name: 'Tech Parks', icon: <Building2 size={40} /> },
    { name: 'Premium Residential', icon: <MapPin size={40} /> }
  ];

  return (
    <div style={{ background: 'var(--bg-primary)', minHeight: '100vh' }}>
      <Header />
      <main style={{ paddingTop: '180px' }}>
        {/* Hero Section */}
        <section style={{
          background: 'linear-gradient(135deg, #f4ede2 0%, #efe4d0 45%, #e6dcc7 100%)',
          padding: '100px 7.6923%',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Background Pattern */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url(https://images.unsplash.com/photo-1519567241046-7f570eee3ce6?w=1920)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.08
          }} />

          <div style={{ maxWidth: '1400px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
            <Link to="/" style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              color: '#ff6b00',
              textDecoration: 'none',
              fontSize: '16px',
              marginBottom: '30px',
              transition: 'all 0.3s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateX(-5px)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateX(0)'}
            >
              <ArrowLeft size={20} />
              Back to VDM Solutions
            </Link>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '60px', alignItems: 'center' }} className="detail-grid">
              <div>
                {/* Zenthara Logo */}
                <img 
                  src="https://customer-assets.emergentagent.com/job_biz-innovations/artifacts/m9542jkn_Zenthara%20Final%20Logo.jpeg" 
                  alt="Zenthara" 
                  style={{ height: '120px', objectFit: 'contain', marginBottom: '20px' }}
                />
                <p style={{ 
                  color: '#ff6b00', 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  letterSpacing: '2px',
                  marginBottom: '20px'
                }}>
                  A DIVISION OF VDM BUSINESS INNOVATIONS
                </p>
                <h1 style={{ 
                  fontSize: '48px', 
                  fontWeight: '700', 
                  color: '#0E2A47',
                  marginBottom: '20px',
                  lineHeight: '1.2'
                }}>
                  Elevating <span style={{ color: '#ff6b00' }}>Commercial Horizons</span>
                </h1>
                <p style={{ 
                  fontSize: '18px', 
                  color: 'rgba(14, 42, 71, 0.78)',
                  marginBottom: '40px',
                  lineHeight: '1.8'
                }}>
                  End-to-end mall management, retail leasing, brand placements, space-on-hire, sponsorship &amp; brand alliances, and digital-media solutions for malls, tech parks, retail destinations and premium commercial &amp; residential projects.
                </p>
                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                  <a href="#services" style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '16px 32px',
                    background: '#ff6b00',
                    color: '#ffffff',
                    textDecoration: 'none',
                    fontWeight: '600',
                    fontSize: '16px',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.3s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#e55a00'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#ff6b00'}
                  >
                    Explore Services
                    <ArrowRight size={20} />
                  </a>
                  <a href="#contact" style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '16px 32px',
                    background: 'transparent',
                    color: '#0E2A47',
                    textDecoration: 'none',
                    fontWeight: '600',
                    fontSize: '16px',
                    border: '2px solid #0E2A47',
                    cursor: 'pointer',
                    transition: 'all 0.3s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#0E2A47';
                    e.currentTarget.style.color = '#ffffff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = '#0E2A47';
                  }}
                  >
                    Contact Us
                  </a>
                </div>
              </div>
              <div style={{ position: 'relative' }}>
                <img 
                  src="https://images.unsplash.com/photo-1519567241046-7f570eee3ce6?w=800&q=80" 
                  alt="Modern Mall Interior" 
                  style={{ 
                    width: '100%', 
                    height: '500px', 
                    objectFit: 'cover',
                    border: '4px solid rgba(255, 107, 0, 0.3)'
                  }}
                />
                {/* Stats Overlay */}
                <div style={{
                  position: 'absolute',
                  bottom: '-30px',
                  left: '20px',
                  right: '20px',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '20px'
                }}>
                  <div style={{
                    background: '#004aad',
                    padding: '20px',
                    textAlign: 'center',
                    border: '2px solid #ff6b00'
                  }}>
                    <div style={{ fontSize: '32px', fontWeight: '700', color: '#ff6b00' }}>29+</div>
                    <div style={{ fontSize: '12px', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '1px' }}>Years Expertise</div>
                  </div>
                  <div style={{
                    background: '#004aad',
                    padding: '20px',
                    textAlign: 'center',
                    border: '2px solid #ff6b00'
                  }}>
                    <div style={{ fontSize: '32px', fontWeight: '700', color: '#ff6b00' }}>100+</div>
                    <div style={{ fontSize: '12px', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '1px' }}>Brand Partners</div>
                  </div>
                  <div style={{
                    background: '#004aad',
                    padding: '20px',
                    textAlign: 'center',
                    border: '2px solid #ff6b00'
                  }}>
                    <div style={{ fontSize: '32px', fontWeight: '700', color: '#ff6b00' }}>Pan</div>
                    <div style={{ fontSize: '12px', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '1px' }}>India Presence</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════
             Mall Management by VDM  (NEW SERVICE VERTICAL)
             ═══════════════════════════════════════════════════════════════ */}
        <section
          id="mall-management"
          aria-label="VDM Business Innovations mall management and retail operations services"
          style={{ background: 'var(--bg-primary)', padding: '100px 7.6923%' }}
        >
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '60px' }}>
              <div style={{
                display: 'inline-block',
                padding: '8px 18px',
                background: 'rgba(255, 107, 0, 0.1)',
                border: '1px solid #ff6b00',
                color: '#ff6b00',
                fontSize: '13px',
                letterSpacing: '3px',
                fontWeight: '600',
                marginBottom: '20px'
              }}>
                SERVICE VERTICAL
              </div>
              <h2 style={{
                fontSize: '42px',
                fontWeight: '700',
                marginBottom: '14px',
                color: 'var(--text-primary)'
              }}>
                Mall Management <span style={{ color: '#ff6b00' }}>by Zenthara from VDM</span>
              </h2>
              <p style={{ fontSize: '18px', color: 'var(--text-secondary)', maxWidth: '860px', margin: '0 auto 24px' }}>
                Integrated Mall Development, Leasing, Operations and Growth Management
              </p>
              <p style={{ fontSize: '15px', color: 'var(--text-muted)', maxWidth: '960px', margin: '0 auto', lineHeight: '1.75' }}>
                VDM Business Innovations provides end-to-end mall-management and commercial advisory services for new and existing retail destinations. Our role extends from mall development planning and retail leasing to pre-opening preparation, successful inauguration, day-to-day operations, tenant management, marketing, customer experience, and long-term commercial growth. VDM works as an integrated extension of the mall owner, developer, and project stakeholders to establish efficient systems, attract the right tenant mix, create a strong customer proposition, and ensure the successful execution and operation of the mall.
              </p>
            </div>

            {/* Complete Mall Management Services */}
            <h3 style={{
              fontSize: '26px',
              fontWeight: '600',
              color: 'var(--text-primary)',
              marginBottom: '30px',
              textAlign: 'center'
            }}>
              Complete Mall Management Services
            </h3>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: '24px',
              marginBottom: '60px'
            }}>
              {[
                { icon: <Building size={32} />, title: 'Mall Development Consultancy', points: [
                  'Commercial planning for new mall developments',
                  'Retail positioning and destination strategy',
                  'Review of mall layout, circulation, access, parking, and customer movement',
                  'Planning of retail, F&B, entertainment, lifestyle, wellness, and service zones',
                  'Development of commercially viable mall concepts and operating structures',
                  'Advisory support for project planning, execution, and stakeholder coordination'
                ] },
                { icon: <Handshake size={32} />, title: 'Retail Leasing & Tenant Mix', points: [
                  'Mall leasing strategy and category planning',
                  'Anchor, vanilla, kiosk, pop-up, and space-on-hire leasing',
                  'Tenant identification, brand approach, negotiations, and onboarding',
                  'Tenant-mix planning across retail, F&B, entertainment, services, and lifestyle categories',
                  'Commercial negotiations and lease coordination',
                  'Occupancy enhancement and long-term revenue planning'
                ] },
                { icon: <ClipboardList size={32} />, title: 'Pre-Opening Mall Management', points: [
                  'Preparation of pre-opening plans and operational timelines',
                  'Shop-opening and tenant fit-out coordination',
                  'Common-area and facility-readiness reviews',
                  'Development of operating procedures, checklists, reporting formats, and escalation systems',
                  'Recruitment, mobilisation, training, and deployment of mall-management teams',
                  'Coordination with tenants, vendors, contractors, consultants, and statutory agencies'
                ] },
                { icon: <PartyPopper size={32} />, title: 'Mall Inauguration & Launch Management', points: [
                  'Planning and execution support for mall inauguration',
                  'Launch-event planning and stakeholder coordination',
                  'Tenant participation and brand activation',
                  'Media, PR, promotional, and customer-engagement activities',
                  'Opening-day operations and visitor-management planning',
                  'Post-launch review and operational improvement'
                ] },
                { icon: <Cog size={32} />, title: 'Complete Mall Operations', points: [
                  'Day-to-day mall management and supervision',
                  'Common-area management and presentation',
                  'Tenant coordination and relationship management',
                  'Coordination of housekeeping, security, engineering, maintenance, parking, utilities, and customer services',
                  'Vendor and service-provider management',
                  'Daily inspections, issue tracking, preventive-maintenance coordination, and reporting',
                  'Monitoring of cleanliness, safety, ambience, signage, lighting, and service quality'
                ] },
                { icon: <Users size={32} />, title: 'Tenant & Commercial Management', points: [
                  'Regular tenant communication and business coordination',
                  'Monitoring of tenant operations and lease compliance',
                  'Support for tenant sales, trading performance, and operational issues',
                  'Coordination of billing, collections, CAM, utilities, and other commercial processes',
                  'Monitoring of occupancy, revenue opportunities, trading density, and tenant productivity',
                  'Tenant retention and relationship-building initiatives'
                ] },
                { icon: <Megaphone size={32} />, title: 'Mall Marketing, Events & Customer Experience', points: [
                  'Annual and monthly mall-marketing plans',
                  'Festivals, events, activations, exhibitions, and promotional campaigns',
                  'Brand alliances, sponsorships, and strategic partnerships',
                  'Customer-footfall and engagement programmes',
                  'Digital, social-media, local-area, and community outreach',
                  'Customer feedback, service improvement, and destination-building initiatives'
                ] },
                { icon: <ShieldCheck size={32} />, title: 'Safety, Compliance & Risk Management', points: [
                  'Fire and life-safety coordination',
                  'Emergency-response planning and drills',
                  'Operational and statutory-compliance monitoring',
                  'Incident reporting and corrective-action tracking',
                  'Vendor documentation and risk-management coordination',
                  'Support for maintaining a safe, compliant, and professionally managed environment'
                ] },
                { icon: <Leaf size={32} />, title: 'Sustainability & ESG Management', points: [
                  'Waste segregation and responsible waste-management systems',
                  'Energy, water, and resource-efficiency initiatives',
                  'Sustainable vendor and operational practices',
                  'Environmental, social, and governance-oriented programmes',
                  'Sustainability awareness initiatives for tenants, employees, vendors, and customers'
                ] },
                { icon: <BarChart3 size={32} />, title: 'Performance Monitoring & Continuous Improvement', points: [
                  'Management dashboards and operating reports',
                  'Monitoring of footfall, occupancy, tenant performance, sales, events, complaints, and service levels',
                  'Periodic stakeholder-review meetings',
                  'Customer-experience measurement and improvement',
                  'Corrective-action planning and implementation',
                  'Continuous improvement of mall operations, commercial performance, and customer satisfaction'
                ] },
              ].map((svc) => (
                <CollapsibleMallCard key={svc.title} svc={svc} previewCount={2} />
              ))}
            </div>

            {/* Highlighted statement */}
            <div style={{
              padding: '32px 40px',
              background: 'rgba(255, 107, 0, 0.08)',
              borderLeft: '4px solid #ff6b00',
              marginBottom: '60px'
            }}>
              <p style={{ fontSize: '17px', color: 'var(--text-primary)', lineHeight: '1.75', margin: 0, fontStyle: 'italic' }}>
                From the first stage of mall development to successful inauguration and complete ongoing operations, VDM provides integrated mall-management expertise focused on commercial performance, operational efficiency, tenant success, and customer experience.
              </p>
            </div>

            {/* VDM's Mall Management Approach + Outcomes */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '40px',
              marginBottom: '60px'
            }}>
              <div>
                <h3 style={{ fontSize: '26px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '18px' }}>
                  VDM&apos;s Mall Management Approach
                </h3>
                <p style={{ fontSize: '15px', color: 'var(--text-secondary)', lineHeight: '1.75' }}>
                  Our approach combines commercial strategy, retail leasing, operational systems, tenant relationship management, marketing, customer experience, facility coordination, and performance monitoring under one integrated framework. This enables mall owners and developers to work with a single strategic partner across the complete lifecycle of a retail destination.
                </p>
              </div>
              <div>
                <h3 style={{ fontSize: '20px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '18px' }}>
                  Service Outcomes
                </h3>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {[
                    'Stronger retail positioning and tenant mix',
                    'Efficient pre-opening and launch execution',
                    'Better tenant coordination and retention',
                    'Improved mall presentation and customer experience',
                    'Structured operations and reporting systems',
                    'Greater commercial visibility and revenue opportunities',
                    'Integrated marketing, events, and digital engagement',
                    'Sustainable and compliant mall operations'
                  ].map((o) => (
                    <li key={o} style={{ display: 'flex', gap: '10px', marginBottom: '10px', color: 'var(--text-secondary)', fontSize: '14px', lineHeight: '1.55' }}>
                      <CheckCircle2 size={16} style={{ color: '#ff6b00', flexShrink: 0, marginTop: '3px' }} />
                      <span>{o}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Recent Engagement (Ferro Hub) — small case-study card only */}
            <div
              data-testid="mall-mgmt-engagement-callout"
              style={{
                padding: '28px 32px',
                background: 'rgba(255, 255, 255, 0.04)',
                border: '1px dashed rgba(255, 107, 0, 0.45)',
                marginBottom: '40px'
              }}
            >
              <div style={{
                fontSize: '11px',
                letterSpacing: '3px',
                color: '#ff6b00',
                fontWeight: '600',
                marginBottom: '10px'
              }}>
                RECENT MALL MANAGEMENT ENGAGEMENT
              </div>
              <p style={{ fontSize: '15px', color: 'var(--text-secondary)', lineHeight: '1.7', margin: 0 }}>
                VDM is currently involved in the complete mall-management and operational execution of <strong style={{ color: 'var(--text-primary)' }}>Ferro Hub Mall, Budigere, Bengaluru</strong>. The engagement includes development advisory, retail leasing, pre-opening planning, inauguration support, tenant coordination, and ongoing mall operations.
              </p>
            </div>

            {/* CTAs */}
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', justifyContent: 'center' }}>
              <a
                href="#contact"
                data-testid="cta-discuss-mall-management"
                onClick={(e) => { e.preventDefault(); document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }); }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '14px 26px',
                  background: '#ff6b00',
                  color: '#ffffff',
                  fontWeight: '600',
                  fontSize: '15px',
                  textDecoration: 'none',
                  transition: 'transform 0.2s ease'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                Discuss Mall Management <ArrowRight size={18} />
              </a>
              <a
                href="#contact"
                data-testid="cta-speak-to-vdm-mall"
                onClick={(e) => { e.preventDefault(); document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }); }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '14px 26px',
                  background: 'transparent',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-medium)',
                  fontWeight: '600',
                  fontSize: '15px',
                  textDecoration: 'none',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#ff6b00'; e.currentTarget.style.color = '#ff6b00'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border-medium)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
              >
                Speak to VDM
              </a>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════════
             Digital Media by VDM  (NEW SERVICE VERTICAL)
             ═══════════════════════════════════════════════════════════════ */}
        <section
          id="digital-media"
          aria-label="Digital media and marketing services by VDM Business Innovations"
          style={{ background: 'var(--bg-primary)', padding: '100px 7.6923%' }}
        >
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '60px' }}>
              <div style={{
                display: 'inline-block',
                padding: '8px 18px',
                background: 'rgba(255, 107, 0, 0.1)',
                border: '1px solid #ff6b00',
                color: '#ff6b00',
                fontSize: '13px',
                letterSpacing: '3px',
                fontWeight: '600',
                marginBottom: '20px'
              }}>
                SERVICE VERTICAL
              </div>
              <h2 style={{
                fontSize: '42px',
                fontWeight: '700',
                marginBottom: '14px',
                color: 'var(--text-primary)'
              }}>
                Digital Media <span style={{ color: '#ff6b00' }}>by Zenthara from VDM</span>
              </h2>
              <p style={{ fontSize: '18px', color: 'var(--text-secondary)', maxWidth: '860px', margin: '0 auto 24px' }}>
                Digital Communication, Content and Media Solutions for Businesses and Retail Destinations
              </p>
              <p style={{ fontSize: '15px', color: 'var(--text-muted)', maxWidth: '960px', margin: '0 auto', lineHeight: '1.75' }}>
                Zenthara also undertakes digital-media services from VDM Business Innovations, supporting businesses, brands, malls, property owners, and commercial destinations with strategic digital communication and media solutions. The service is designed to improve brand visibility, customer engagement, online presence, campaign performance, and communication across digital platforms.
              </p>
            </div>

            {/* Digital Media Service Cards */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
              gap: '24px',
              marginBottom: '50px'
            }}>
              {[
                { icon: <Target size={32} />, title: 'Digital Marketing Strategy', desc: 'End-to-end digital strategy tailored to business, brand, or destination goals.' },
                { icon: <Lightbulb size={32} />, title: 'Campaign Concepts & Promotional Creatives', desc: 'Concept development and creative production for campaigns that convert.' },
                { icon: <Store size={32} />, title: 'Mall & Retail-Destination Promotions', desc: 'Purpose-built digital promotions for malls and retail properties.' },
                { icon: <Calendar size={32} />, title: 'Event & Activation Promotion', desc: 'Digital amplification for launches, events, activations and exhibitions.' },
                { icon: <LineChart size={32} />, title: 'Digital Media Planning & Performance Reporting', desc: 'Structured media plans with transparent performance tracking and reporting.' },
              ].map((svc) => (
                <div
                  key={svc.title}
                  data-testid={`digital-media-card-${svc.title}`}
                  style={{
                    padding: '32px 28px',
                    background: 'rgba(255, 255, 255, 0.03)',
                    border: '1px solid rgba(255, 107, 0, 0.3)',
                    transition: 'all 0.3s ease-in-out'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#ff6b00';
                    e.currentTarget.style.background = 'rgba(255, 107, 0, 0.06)';
                    e.currentTarget.style.transform = 'translateY(-4px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(255, 107, 0, 0.3)';
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.03)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <div style={{ color: '#ff6b00', marginBottom: '16px' }}>{svc.icon}</div>
                  <h4 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '10px' }}>
                    {svc.title}
                  </h4>
                  <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: '1.65', margin: 0 }}>
                    {svc.desc}
                  </p>
                </div>
              ))}
            </div>

            {/* Closing copy */}
            <div style={{
              padding: '32px 40px',
              background: 'rgba(255, 107, 0, 0.08)',
              borderLeft: '4px solid #ff6b00',
              marginBottom: '40px'
            }}>
              <p style={{ fontSize: '16px', color: 'var(--text-primary)', lineHeight: '1.75', margin: 0 }}>
                Through Zenthara, VDM brings together commercial expertise, retail strategy, mall management, space-leasing capabilities, SOH solutions, and digital-media support to help businesses and destinations build stronger market visibility and long-term growth.
              </p>
            </div>

            {/* CTA */}
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', justifyContent: 'center' }}>
              <a
                href="#contact"
                data-testid="cta-explore-digital-media"
                onClick={(e) => { e.preventDefault(); document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }); }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '14px 26px',
                  background: '#ff6b00',
                  color: '#ffffff',
                  fontWeight: '600',
                  fontSize: '15px',
                  textDecoration: 'none',
                  transition: 'transform 0.2s ease'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                Explore Digital Media Services <ArrowRight size={18} />
              </a>
              <a
                href="#contact"
                data-testid="cta-speak-to-vdm-digital"
                onClick={(e) => { e.preventDefault(); document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }); }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '14px 26px',
                  background: 'transparent',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-medium)',
                  fontWeight: '600',
                  fontSize: '15px',
                  textDecoration: 'none',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#ff6b00'; e.currentTarget.style.color = '#ff6b00'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border-medium)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
              >
                Speak to VDM
              </a>
            </div>
          </div>
        </section>

        {/* Sponsorship & Brand Alliances Section */}
        <section style={{ background: 'var(--bg-primary)', padding: '100px 7.6923%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '60px',
              alignItems: 'center'
            }} className="sponsorship-grid">
              <div>
                <div style={{
                  display: 'inline-block',
                  padding: '8px 16px',
                  background: 'rgba(255, 107, 0, 0.1)',
                  border: '1px solid #ff6b00',
                  marginBottom: '20px',
                  fontSize: '14px',
                  color: '#ff6b00',
                  fontWeight: '500'
                }}>
                  NEW VERTICAL
                </div>
                <h2 className="display-medium" style={{ marginBottom: '24px' }}>
                  Zenthara <span style={{ color: '#ff6b00' }}>Sponsorship & Brand Alliances</span>
                </h2>
                <p className="body-large" style={{ color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '32px' }}>
                  Zenthara Sponsorship & Brand Alliances is a specialized vertical that connects organizations and event IPs with the right brands and sponsors. We design and manage strategic sponsorships that deliver visibility, engagement, and measurable outcomes for all stakeholders in the retail ecosystem.
                </p>
                <Link to="/zenthara/sponsorship" style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '16px 32px',
                  background: '#ff6b00',
                  color: '#ffffff',
                  textDecoration: 'none',
                  fontWeight: '600',
                  fontSize: '16px',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.3s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#e55a00'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#ff6b00'}
                >
                  Know more
                  <ArrowRight size={20} />
                </Link>
              </div>
              <div style={{
                position: 'relative',
                padding: '40px',
                background: 'linear-gradient(135deg, rgba(0, 74, 173, 0.1) 0%, rgba(0, 74, 173, 0.05) 100%)',
                border: '2px solid rgba(255, 107, 0, 0.3)'
              }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '24px'
                }}>
                  <div style={{ textAlign: 'center', padding: '24px', background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-subtle)' }}>
                    <Handshake size={40} color="#ff6b00" style={{ marginBottom: '12px' }} />
                    <h4 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '8px' }}>Brand Partnerships</h4>
                    <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Strategic sponsor-brand matching</p>
                  </div>
                  <div style={{ textAlign: 'center', padding: '24px', background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-subtle)' }}>
                    <Calendar size={40} color="#ff6b00" style={{ marginBottom: '12px' }} />
                    <h4 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '8px' }}>Event Sponsorships</h4>
                    <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>End-to-end sponsorship support</p>
                  </div>
                  <div style={{ textAlign: 'center', padding: '24px', background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-subtle)' }}>
                    <TrendingUp size={40} color="#ff6b00" style={{ marginBottom: '12px' }} />
                    <h4 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '8px' }}>Measurable ROI</h4>
                    <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Outcome-driven sponsorships</p>
                  </div>
                  <div style={{ textAlign: 'center', padding: '24px', background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-subtle)' }}>
                    <Users size={40} color="#ff6b00" style={{ marginBottom: '12px' }} />
                    <h4 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '8px' }}>Network Access</h4>
                    <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Connect with decision-makers</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section id="services" style={{ background: 'var(--bg-primary)', padding: '100px 7.6923%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '60px' }}>
              <div style={{
                display: 'inline-block',
                padding: '8px 16px',
                background: 'rgba(255, 107, 0, 0.1)',
                border: '1px solid #ff6b00',
                marginBottom: '20px',
                fontSize: '14px',
                color: '#ff6b00',
                fontWeight: '500'
              }}>
                OUR SERVICES
              </div>
              <h2 className="display-medium" style={{ marginBottom: '20px' }}>
                Comprehensive <span style={{ color: '#ff6b00' }}>Retail Solutions</span>
              </h2>
              <p className="body-medium" style={{ maxWidth: '800px', margin: '0 auto', color: 'var(--text-muted)' }}>
                End-to-end services designed to maximize retail potential and drive commercial success
              </p>
            </div>

            {/* 2x2 Services Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '30px'
            }} className="services-grid">
              {services.map((service, index) => (
                <div key={index} style={{
                  padding: '50px',
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid var(--border-subtle)',
                  transition: 'all 0.3s ease-in-out'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#ff6b00';
                  e.currentTarget.style.transform = 'translateY(-5px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--border-subtle)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
                >
                  <div style={{
                    width: '80px',
                    height: '80px',
                    background: 'linear-gradient(135deg, #004aad 0%, #0066cc 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '24px',
                    color: '#ffffff'
                  }}>
                    {service.icon}
                  </div>
                  <h3 className="heading-1" style={{ marginBottom: '16px', color: '#ff6b00' }}>
                    {service.title}
                  </h3>
                  <p className="body-medium" style={{ marginBottom: '24px', color: 'var(--text-muted)' }}>
                    {service.description}
                  </p>
                  <ul style={{ listStyle: 'none', padding: 0 }}>
                    {service.features.map((feature, idx) => (
                      <li key={idx} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '12px'
                      }}>
                        <CheckCircle2 size={18} color="#ff6b00" />
                        <span className="body-small">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Sectors We Serve */}
        <section style={{ background: 'var(--bg-primary)', padding: '100px 7.6923%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '60px' }}>
              <h2 className="display-medium" style={{ marginBottom: '20px' }}>
                Sectors We <span style={{ color: '#ff6b00' }}>Serve</span>
              </h2>
              <p className="body-medium" style={{ color: 'var(--text-muted)' }}>
                Comprehensive retail solutions across diverse commercial sectors
              </p>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '30px'
            }} className="sectors-grid">
              {sectors.map((sector, index) => (
                <div key={index} style={{
                  padding: '50px 30px',
                  background: 'linear-gradient(135deg, rgba(0, 74, 173, 0.1) 0%, rgba(0, 74, 173, 0.05) 100%)',
                  border: '2px solid rgba(0, 74, 173, 0.3)',
                  textAlign: 'center',
                  transition: 'all 0.3s ease-in-out'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#ff6b00';
                  e.currentTarget.style.transform = 'translateY(-10px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(0, 74, 173, 0.3)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
                >
                  <div style={{ color: '#ff6b00', marginBottom: '20px' }}>
                    {sector.icon}
                  </div>
                  <h3 className="heading-2">{sector.name}</h3>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Zenthara Section */}
        <section style={{ 
          background: 'linear-gradient(135deg, #001a3d 0%, #002b5c 100%)', 
          padding: '100px 7.6923%' 
        }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '60px' }}>
              <h2 style={{ 
                fontSize: '42px', 
                fontWeight: '700', 
                color: '#ffffff',
                marginBottom: '20px'
              }}>
                Why Choose <span style={{ color: '#ff6b00' }}>Zenthara</span>?
              </h2>
              <p style={{ fontSize: '18px', color: 'rgba(255,255,255,0.7)', maxWidth: '800px', margin: '0 auto' }}>
                Your trusted partner for revenue and reputation growth
              </p>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '30px'
            }}>
              {strengths.map((strength, index) => (
                <div key={index} style={{
                  padding: '40px 30px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 107, 0, 0.3)',
                  transition: 'all 0.3s ease-in-out'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#ff6b00';
                  e.currentTarget.style.background = 'rgba(255, 107, 0, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(255, 107, 0, 0.3)';
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                }}
                >
                  <div style={{ color: '#ff6b00', marginBottom: '20px' }}>
                    {strength.icon}
                  </div>
                  <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#ffffff', marginBottom: '12px' }}>
                    {strength.title}
                  </h3>
                  <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', lineHeight: '1.6' }}>
                    {strength.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Portfolio/Projects Placeholder */}
        <section style={{ background: 'var(--bg-primary)', padding: '100px 7.6923%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '60px' }}>
              <h2 className="display-medium" style={{ marginBottom: '20px' }}>
                Our <span style={{ color: '#ff6b00' }}>Portfolio</span>
              </h2>
              <p className="body-medium" style={{ color: 'var(--text-muted)' }}>
                Successfully partnered with leading malls and commercial spaces across India
              </p>
            </div>

            {/* Portfolio Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '30px'
            }} className="portfolio-grid">
              {[
                { title: 'Premium Malls', image: 'https://images.unsplash.com/photo-1519567241046-7f570eee3ce6?w=600', desc: 'Strategic leasing & brand placement' },
                { title: 'Tech Parks', image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600', desc: 'Retail solutions for tech campuses' },
                { title: 'Mixed-Use Developments', image: 'https://images.unsplash.com/photo-1577587230708-187fdbef4d91?w=600', desc: 'Integrated commercial spaces' }
              ].map((project, index) => (
                <div key={index} style={{
                  position: 'relative',
                  height: '350px',
                  overflow: 'hidden',
                  border: '1px solid var(--border-subtle)',
                  transition: 'all 0.3s ease-in-out'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#ff6b00';
                  e.currentTarget.querySelector('img').style.transform = 'scale(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--border-subtle)';
                  e.currentTarget.querySelector('img').style.transform = 'scale(1)';
                }}
                >
                  <img 
                    src={project.image} 
                    alt={project.title}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.4s ease-in-out'
                    }}
                  />
                  <div style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: 'linear-gradient(transparent, rgba(0, 74, 173, 0.95))',
                    padding: '60px 30px 30px'
                  }}>
                    <h3 style={{ fontSize: '22px', fontWeight: '600', color: '#ffffff', marginBottom: '8px' }}>
                      {project.title}
                    </h3>
                    <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.8)' }}>
                      {project.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" style={{ background: 'var(--bg-primary)', padding: '100px 7.6923%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '60px',
              alignItems: 'start'
            }} className="contact-grid">
              {/* Contact Info */}
              <div>
                <div style={{
                  display: 'inline-block',
                  padding: '8px 16px',
                  background: 'rgba(255, 107, 0, 0.1)',
                  border: '1px solid #ff6b00',
                  marginBottom: '20px',
                  fontSize: '14px',
                  color: '#ff6b00',
                  fontWeight: '500'
                }}>
                  GET IN TOUCH
                </div>
                <h2 className="display-medium" style={{ marginBottom: '20px' }}>
                  Let's <span style={{ color: '#ff6b00' }}>Partner</span> for Growth
                </h2>
                <p className="body-medium" style={{ marginBottom: '40px', color: 'var(--text-muted)' }}>
                  Ready to elevate your commercial space? Contact us for a consultation and discover how Zenthara can maximize your retail potential.
                </p>

                <div style={{ marginBottom: '30px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
                    <div style={{
                      width: '50px',
                      height: '50px',
                      background: 'linear-gradient(135deg, #004aad 0%, #0066cc 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Mail size={24} color="#ffffff" />
                    </div>
                    <div>
                      <p style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '4px' }}>Email Us</p>
                      <a href="mailto:sandeep@vdmbi.org" style={{ fontSize: '16px', color: '#ff6b00', textDecoration: 'none' }}>
                        sandeep@vdmbi.org
                      </a>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
                    <div style={{
                      width: '50px',
                      height: '50px',
                      background: 'linear-gradient(135deg, #004aad 0%, #0066cc 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <MapPin size={24} color="#ffffff" />
                    </div>
                    <div>
                      <p style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '4px' }}>Location</p>
                      <p style={{ fontSize: '16px', color: 'var(--text-primary)' }}>
                        Bengaluru, Karnataka, India
                      </p>
                    </div>
                  </div>
                </div>

                {/* NDA Document Card */}
                <div data-testid="zenthara-nda-card" style={{
                  padding: '24px',
                  background: 'rgba(255, 107, 0, 0.06)',
                  border: '1px solid rgba(255, 107, 0, 0.35)',
                  marginBottom: '30px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', marginBottom: '18px' }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      background: 'linear-gradient(135deg, #ff6b00 0%, #ff8c1a 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <FileText size={22} color="#ffffff" />
                    </div>
                    <div>
                      <h4 style={{ fontSize: '17px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '4px' }}>
                        Non-Disclosure Agreement
                      </h4>
                      <p style={{ fontSize: '13px', color: 'var(--text-muted)', lineHeight: 1.6 }}>
                        Download our standard NDA for partnership discussions, brand alliances, or commercial engagements with Zenthara.
                      </p>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <a
                      href={NDA_URL}
                      download="VDM-Zenthara-NDA.pdf"
                      data-testid="zenthara-nda-download-btn"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 18px',
                        background: '#ff6b00',
                        color: '#ffffff',
                        fontSize: '13px',
                        fontWeight: '600',
                        textDecoration: 'none',
                        letterSpacing: '0.5px',
                        transition: 'all 0.3s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#ff8c1a';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#ff6b00';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      <Download size={15} />
                      Download NDA
                    </a>
                    <a
                      href={NDA_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="zenthara-nda-view-btn"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 18px',
                        background: 'transparent',
                        color: '#ff6b00',
                        fontSize: '13px',
                        fontWeight: '600',
                        textDecoration: 'none',
                        border: '1px solid #ff6b00',
                        letterSpacing: '0.5px',
                        transition: 'all 0.3s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 107, 0, 0.12)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      View PDF
                    </a>
                    <button
                      type="button"
                      onClick={handleCopyNdaLink}
                      data-testid="zenthara-nda-copy-btn"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 18px',
                        background: 'transparent',
                        color: ndaCopied ? '#00ffd1' : 'var(--text-secondary)',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        border: `1px solid ${ndaCopied ? '#00ffd1' : 'var(--border-subtle)'}`,
                        letterSpacing: '0.5px',
                        transition: 'all 0.3s'
                      }}
                    >
                      <Copy size={15} />
                      {ndaCopied ? 'Link Copied!' : 'Copy Link for Email'}
                    </button>
                  </div>

                  <div style={{
                    marginTop: '14px',
                    padding: '10px 12px',
                    background: 'rgba(0, 0, 0, 0.25)',
                    border: '1px dashed var(--border-subtle)',
                    fontSize: '12px',
                    color: 'var(--text-muted)',
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    wordBreak: 'break-all'
                  }} data-testid="zenthara-nda-url">
                    {NDA_FULL_URL}
                  </div>
                </div>

                {/* Map Placeholder */}
                <div style={{
                  width: '100%',
                  height: '250px',
                  background: 'rgba(0, 74, 173, 0.1)',
                  border: '1px solid rgba(0, 74, 173, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d497698.7495234747!2d77.30126147656246!3d12.954459399999999!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae1670c9b44e6d%3A0xf8dfc3e8517e4fe0!2sBengaluru%2C%20Karnataka!5e0!3m2!1sen!2sin!4v1702000000000!5m2!1sen!2sin"
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 0,
                      filter: 'grayscale(100%) invert(92%) contrast(83%)'
                    }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Zenthara Location"
                  />
                </div>
              </div>

              {/* Contact Form */}
              <div style={{
                padding: '50px',
                background: 'rgba(0, 74, 173, 0.05)',
                border: '2px solid rgba(0, 74, 173, 0.3)'
              }}>
                <h3 className="heading-1" style={{ marginBottom: '30px' }}>
                  Send Us a Message
                </h3>
                <form>
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: 'var(--text-muted)' }}>
                      Your Name *
                    </label>
                    <input 
                      type="text" 
                      placeholder="Enter your name"
                      style={{
                        width: '100%',
                        padding: '14px 16px',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid var(--border-subtle)',
                        color: 'var(--text-primary)',
                        fontSize: '16px',
                        outline: 'none'
                      }}
                    />
                  </div>
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: 'var(--text-muted)' }}>
                      Email Address *
                    </label>
                    <input 
                      type="email" 
                      placeholder="Enter your email"
                      style={{
                        width: '100%',
                        padding: '14px 16px',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid var(--border-subtle)',
                        color: 'var(--text-primary)',
                        fontSize: '16px',
                        outline: 'none'
                      }}
                    />
                  </div>
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: 'var(--text-muted)' }}>
                      Property/Project Type
                    </label>
                    <select
                      style={{
                        width: '100%',
                        padding: '14px 16px',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid var(--border-subtle)',
                        color: 'var(--text-primary)',
                        fontSize: '16px',
                        outline: 'none'
                      }}
                    >
                      <option value="">Select property type</option>
                      <option value="mall">Shopping Mall</option>
                      <option value="techpark">Tech Park</option>
                      <option value="residential">Premium Residential</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: 'var(--text-muted)' }}>
                      Message *
                    </label>
                    <textarea 
                      placeholder="Tell us about your project..."
                      rows={5}
                      style={{
                        width: '100%',
                        padding: '14px 16px',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid var(--border-subtle)',
                        color: 'var(--text-primary)',
                        fontSize: '16px',
                        outline: 'none',
                        resize: 'vertical'
                      }}
                    />
                  </div>
                  <button 
                    type="submit"
                    style={{
                      width: '100%',
                      padding: '16px 32px',
                      background: '#ff6b00',
                      color: '#ffffff',
                      fontWeight: '600',
                      fontSize: '16px',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'all 0.3s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '10px'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#e55a00'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#ff6b00'}
                  >
                    Submit Inquiry
                    <ArrowRight size={20} />
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section style={{ 
          background: 'linear-gradient(135deg, #ff6b00 0%, #e55a00 100%)', 
          padding: '80px 7.6923%' 
        }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto', textAlign: 'center' }}>
            <h2 style={{ 
              fontSize: '36px', 
              fontWeight: '700', 
              color: '#ffffff',
              marginBottom: '20px'
            }}>
              Ready to Elevate Your Commercial Space?
            </h2>
            <p style={{ 
              fontSize: '18px', 
              color: 'rgba(255,255,255,0.9)',
              marginBottom: '40px',
              maxWidth: '700px',
              margin: '0 auto 40px'
            }}>
              Partner with Zenthara for intelligent leasing solutions that drive revenue and reputation growth.
            </p>
            <Link to="/#contact" style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px',
              padding: '16px 40px',
              background: '#004aad',
              color: '#ffffff',
              textDecoration: 'none',
              fontWeight: '600',
              fontSize: '16px',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.3s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#003380'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#004aad'}
            >
              Schedule a Consultation
              <ArrowRight size={20} />
            </Link>
          </div>
        </section>
      </main>
      <Footer />

      <style>{`
        @media (max-width: 1024px) {
          .services-grid {
            grid-template-columns: 1fr !important;
          }
          .portfolio-grid {
            grid-template-columns: 1fr !important;
          }
          .sponsorship-grid {
            grid-template-columns: 1fr !important;
          }
        }
        @media (max-width: 768px) {
          .detail-grid {
            grid-template-columns: 1fr !important;
          }
          .sectors-grid {
            grid-template-columns: 1fr !important;
          }
          .contact-grid {
            grid-template-columns: 1fr !important;
          }
          .sponsorship-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ZentharaDetail;
